/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { roundTo } from "@/lib/utils/domain/calculation-helpers";

// ============================================================================
// REALTIME METRICS - Event Rate and Performance Tracking
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

export interface RealtimeMetrics {
  eventsPerSecond: number;
  dbCallsPerSecond: number;
  apiCallsPerSecond: number;
  totalEvents: number;
  totalDbCalls: number;
  totalApiCalls: number;
  subscriptions: number;
  errors: number;
  reconnects: number;
  lastUpdated: number;
}

export interface RealtimeEventRecord {
  timestamp: number;
  organizationId: string;
  table: string;
  eventType: "INSERT" | "UPDATE" | "DELETE" | "UNKNOWN";
  triggeredDbCall: boolean;
  triggeredApiCall: boolean;
}

export interface RealtimeMetricsByOrg {
  [organizationId: string]: {
    eventsPerSecond: number;
    dbCallsPerSecond: number;
    apiCallsPerSecond: number;
    totalEvents: number;
    totalDbCalls: number;
    totalApiCalls: number;
    subscriptions: number;
    errors: number;
    reconnects: number;
    lastUpdated: number;
  };
}

// ============================================================================
// METRICS COLLECTOR
// ============================================================================

class RealtimeMetricsCollector {
  private static instance: RealtimeMetricsCollector;
  private events: RealtimeEventRecord[] = [];
  private dbCalls: Array<{ timestamp: number; organizationId: string }> = [];
  private apiCalls: Array<{ timestamp: number; organizationId: string }> = [];
  private subscriptions = new Map<string, number>();
  private errors = new Map<string, number>();
  private reconnects = new Map<string, number>();
  private readonly windowSize = 60000; // 60 seconds
  private readonly maxEvents = 10000; // Keep last 10k events

  static getInstance(): RealtimeMetricsCollector {
    if (!RealtimeMetricsCollector.instance) {
      RealtimeMetricsCollector.instance = new RealtimeMetricsCollector();
    }
    return RealtimeMetricsCollector.instance;
  }

  // ============================================================================
  // EVENT TRACKING
  // ============================================================================

  recordEvent(
    organizationId: string,
    table: string,
    eventType: "INSERT" | "UPDATE" | "DELETE" | "UNKNOWN",
    triggeredDbCall = false,
    triggeredApiCall = false,
  ): void {
    const now = Date.now();
    this.events.push({
      timestamp: now,
      organizationId,
      table,
      eventType,
      triggeredDbCall,
      triggeredApiCall,
    });

    if (triggeredDbCall) {
      this.dbCalls.push({ timestamp: now, organizationId });
    }

    if (triggeredApiCall) {
      this.apiCalls.push({ timestamp: now, organizationId });
    }

    // Cleanup old events
    this.cleanup();
  }

  recordSubscription(organizationId: string, table: string): void {
    const key = `${organizationId}_${table}`;
    const current = this.subscriptions.get(key) ?? 0;
    this.subscriptions.set(key, current + 1);
  }

  recordUnsubscription(organizationId: string, table: string): void {
    const key = `${organizationId}_${table}`;
    const current = this.subscriptions.get(key) ?? 0;
    if (current > 0) {
      this.subscriptions.set(key, current - 1);
    }
  }

  recordError(organizationId: string, table: string): void {
    const key = `${organizationId}_${table}`;
    const current = this.errors.get(key) ?? 0;
    this.errors.set(key, current + 1);
  }

  recordReconnect(organizationId: string, table: string): void {
    const key = `${organizationId}_${table}`;
    const current = this.reconnects.get(key) ?? 0;
    this.reconnects.set(key, current + 1);
  }

  // ============================================================================
  // METRICS CALCULATION
  // ============================================================================

  getMetrics(organizationId?: string): RealtimeMetrics {
    const now = Date.now();
    const windowStart = now - this.windowSize;

    // Filter events within window
    const recentEvents = this.events.filter((e) => {
      const inWindow = e.timestamp >= windowStart;
      return organizationId
        ? inWindow && e.organizationId === organizationId
        : inWindow;
    });

    const recentDbCalls = this.dbCalls.filter((c) => {
      const inWindow = c.timestamp >= windowStart;
      return organizationId
        ? inWindow && c.organizationId === organizationId
        : inWindow;
    });

    const recentApiCalls = this.apiCalls.filter((c) => {
      const inWindow = c.timestamp >= windowStart;
      return organizationId
        ? inWindow && c.organizationId === organizationId
        : inWindow;
    });

    // Calculate rates per second
    const eventsPerSecond = recentEvents.length / (this.windowSize / 1000);
    const dbCallsPerSecond = recentDbCalls.length / (this.windowSize / 1000);
    const apiCallsPerSecond = recentApiCalls.length / (this.windowSize / 1000);

    // Count active subscriptions
    let subscriptions = 0;
    if (organizationId) {
      for (const [key, count] of this.subscriptions.entries()) {
        if (key.startsWith(`${organizationId}_`) && count > 0) {
          subscriptions += count;
        }
      }
    } else {
      for (const count of this.subscriptions.values()) {
        if (count > 0) {
          subscriptions += count;
        }
      }
    }

    // Count errors
    let errors = 0;
    if (organizationId) {
      for (const [key, count] of this.errors.entries()) {
        if (key.startsWith(`${organizationId}_`)) {
          errors += count;
        }
      }
    } else {
      for (const count of this.errors.values()) {
        errors += count;
      }
    }

    // Count reconnects
    let reconnects = 0;
    if (organizationId) {
      for (const [key, count] of this.reconnects.entries()) {
        if (key.startsWith(`${organizationId}_`)) {
          reconnects += count;
        }
      }
    } else {
      for (const count of this.reconnects.values()) {
        reconnects += count;
      }
    }

    return {
      eventsPerSecond: roundTo(eventsPerSecond, 2),
      dbCallsPerSecond: roundTo(dbCallsPerSecond, 2),
      apiCallsPerSecond: roundTo(apiCallsPerSecond, 2),
      totalEvents: this.events.length,
      totalDbCalls: this.dbCalls.length,
      totalApiCalls: this.apiCalls.length,
      subscriptions,
      errors,
      reconnects,
      lastUpdated: now,
    };
  }

  getMetricsByOrg(): RealtimeMetricsByOrg {
    const orgs = new Set<string>();
    for (const event of this.events) {
      if (event.organizationId) {
        orgs.add(event.organizationId);
      }
    }
    for (const [key] of this.subscriptions.entries()) {
      const [orgId] = key.split("_");
      if (orgId) {
        orgs.add(orgId);
      }
    }

    const result: RealtimeMetricsByOrg = {};
    for (const orgId of orgs) {
      result[orgId] = this.getMetrics(orgId);
    }

    return result;
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  private cleanup(): void {
    const now = Date.now();
    const cutoff = now - this.windowSize * 2; // Keep 2x window for safety

    // Cleanup events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.filter((e) => e.timestamp >= cutoff);
    }

    // Cleanup DB calls
    this.dbCalls = this.dbCalls.filter((c) => c.timestamp >= cutoff);

    // Cleanup API calls
    this.apiCalls = this.apiCalls.filter((c) => c.timestamp >= cutoff);

    // OPTIMIZATION: Cleanup Maps to prevent unbounded growth
    // Remove entries with zero counts and limit Map sizes
    const MAX_MAP_SIZE = 500; // Max entries per Map

    // Cleanup subscriptions Map
    if (this.subscriptions.size > MAX_MAP_SIZE) {
      const entries = Array.from(this.subscriptions.entries());
      // Remove entries with zero counts first
      for (const [key, count] of entries) {
        if (count === 0) {
          this.subscriptions.delete(key);
        }
      }
      // If still over limit, remove oldest entries (FIFO)
      if (this.subscriptions.size > MAX_MAP_SIZE) {
        const toRemove = this.subscriptions.size - MAX_MAP_SIZE;
        let removed = 0;
        for (const [key] of this.subscriptions.entries()) {
          if (removed >= toRemove) break;
          this.subscriptions.delete(key);
          removed++;
        }
      }
    }

    // Cleanup errors Map (reset counts periodically to prevent unbounded growth)
    if (this.errors.size > MAX_MAP_SIZE) {
      const entries = Array.from(this.errors.entries());
      // Remove entries with zero counts
      for (const [key, count] of entries) {
        if (count === 0) {
          this.errors.delete(key);
        }
      }
      // If still over limit, remove oldest entries
      if (this.errors.size > MAX_MAP_SIZE) {
        const toRemove = this.errors.size - MAX_MAP_SIZE;
        let removed = 0;
        for (const [key] of this.errors.entries()) {
          if (removed >= toRemove) break;
          this.errors.delete(key);
          removed++;
        }
      }
    }

    // Cleanup reconnects Map
    if (this.reconnects.size > MAX_MAP_SIZE) {
      const entries = Array.from(this.reconnects.entries());
      // Remove entries with zero counts
      for (const [key, count] of entries) {
        if (count === 0) {
          this.reconnects.delete(key);
        }
      }
      // If still over limit, remove oldest entries
      if (this.reconnects.size > MAX_MAP_SIZE) {
        const toRemove = this.reconnects.size - MAX_MAP_SIZE;
        let removed = 0;
        for (const [key] of this.reconnects.entries()) {
          if (removed >= toRemove) break;
          this.reconnects.delete(key);
          removed++;
        }
      }
    }
  }

  // ============================================================================
  // RESET
  // ============================================================================

  reset(): void {
    this.events = [];
    this.dbCalls = [];
    this.apiCalls = [];
    this.subscriptions.clear();
    this.errors.clear();
    this.reconnects.clear();
  }

  resetOrg(organizationId: string): void {
    this.events = this.events.filter(
      (e) => e.organizationId !== organizationId,
    );
    this.dbCalls = this.dbCalls.filter(
      (c) => c.organizationId !== organizationId,
    );
    this.apiCalls = this.apiCalls.filter(
      (c) => c.organizationId !== organizationId,
    );

    for (const [key] of this.subscriptions.entries()) {
      if (key.startsWith(`${organizationId}_`)) {
        this.subscriptions.delete(key);
      }
    }

    for (const [key] of this.errors.entries()) {
      if (key.startsWith(`${organizationId}_`)) {
        this.errors.delete(key);
      }
    }

    for (const [key] of this.reconnects.entries()) {
      if (key.startsWith(`${organizationId}_`)) {
        this.reconnects.delete(key);
      }
    }
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const realtimeMetrics = RealtimeMetricsCollector.getInstance();
